import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import BannerImage from "public/images/norfolktides_flex/Norfolk Attend Banner Image 2025 copy.png";
import Link from "@mui/material/Link";

const config: TeamConfig = {
  teamSchema: "norfolktides_flex",
  browserTitle: "Norfolk Tides",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Norfolk Tides",

  // Seat map configs
  seatMapConfigs: {
    venueId: "nam-us-30770-baseball-sec",
    mapId: "blockmap2d",
    staticSeatMapImage: "", // Not provided
    isShowAvailableSeatsButtonHidden: false,
    isView3dButtonHidden: false,
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You've unlocked 20% off your tickets. Add 2 more games for 25% off or checkout now.",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 25,
        hideOfferFromModal: true,
        customProgressBarMessage: "Add 1 more game for 25% off your tickets.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 25,
        customProgressBarMessage:
          "Congrats! You have unlocked 25% off your tickets! Add more games or checkout now.",
      },
    ],
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 8,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  platform: vendorUUIDs.ticketmaster, // TM platform
  refreshInventoryWhenEventClick: false,
  oktaIssuer: "",
  oktaClientId: "",

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl: "https://am.ticketmaster.com/tides/",

  // Images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: "",
    checkoutStatusPageImage: "",
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/norfolk-tides-logo.png" +
      "?width=128&height=75", // actual size 300x300,
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/norfolk-tides-logo.png",
    logoNameImage: "",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    progressBar: {
      staticProgressBarText: false,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:7576222222"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          (757) 622-2222
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#0d9b4f",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 2 games and get 20% off or 4+ games for 25% off your tickets!",
      progressBarAditionalText: " to continue and receive 20% off!",
    },
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    checkoutSuccessDescription: "Your Norfolk Tides tickets are confirmed!",
    checkoutSuccessMessage: "Your purchase is complete!",
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
